import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Modal } from "components/designV2/modal/modal";
import { PayoutType } from "model/Tip";
import type { TipDetails } from "model/Tip";
import type { TipPayoutResult } from "../../rows/tips.rows.types";
import { formatNumber } from "utils/utils";
import { formatTipDate } from "../../logic/tips.date.logic";
import { PAY_OUT_BUTTON_STYLES } from "../../tips.constants";
import {
  SwishPaymentDetails,
  SwishPaymentError,
  SwishPaymentFooterContent,
  SwishPaymentFooterActions,
  SwishPaymentFooter,
  SwishPaymentLabel,
  SwishPaymentNote,
  SwishPaymentValue,
} from "./tips.swishPaymentModal.styled";

interface PayOutParams {
  readonly companyTipIds: readonly number[];
  readonly payoutType: PayoutType;
}

interface TipsSwishPaymentModalProps {
  readonly isOpen: boolean;
  readonly isSaving: boolean;
  readonly tips: readonly TipDetails[];
  readonly onClose: () => void;
  readonly onPayOut: (params: PayOutParams) => Promise<TipPayoutResult>;
}

export const TipsSwishPaymentModal = (
  props: TipsSwishPaymentModalProps,
): JSX.Element | null => {
  const { i18n, t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!props.isOpen) {
      setErrorMessage(null);
    }
  }, [props.isOpen]);

  if (!props.isOpen || props.tips.length === 0) return null;

  const totalAmount = props.tips.reduce((sum, tip) => sum + tip.amount, 0);
  const recipientsCount = props.tips.reduce(
    (sum, tip) => sum + tip.workers.length,
    0,
  );
  const tipsPeriod =
    props.tips.length === 1
      ? formatTipDate({
          dateValue: props.tips[0].date,
          isYearVisible: true,
          locale: i18n.language,
        })
      : t("Tips.SelectedTipsPeriod", { count: props.tips.length });

  const handlePayOut = async (): Promise<void> => {
    setErrorMessage(null);

    const result = await props.onPayOut({
      companyTipIds: props.tips.map((tip) => tip.companyTipId),
      payoutType: PayoutType.Automatic,
    });

    if (result.ok) {
      props.onClose();
      return;
    }

    setErrorMessage(result.errorMessage);
  };

  return (
    <Modal
      label={t("Tips.SwishPayment")}
      size="compact"
      isOpen={props.isOpen}
      onClose={props.onClose}
      showCloseButton={false}
      footer={
        <SwishPaymentFooterContent>
          <SwishPaymentFooterActions>
            <SecondaryButton
              label={t("Tips.Cancel")}
              onClick={props.onClose}
              size="small"
              type="button"
            />
            <PrimaryButton
              iconName="v2/send"
              isLoading={props.isSaving}
              label={t("Tips.PayOut")}
              onClick={() => {
                void handlePayOut();
              }}
              size="small"
              styleOverrides={PAY_OUT_BUTTON_STYLES}
              type="button"
            />
          </SwishPaymentFooterActions>
          <SwishPaymentFooter>
            <div>
              <SwishPaymentNote>
                {t("Tips.SwishIrreversibleNote")}
              </SwishPaymentNote>
              {errorMessage && (
                <SwishPaymentError>{errorMessage}</SwishPaymentError>
              )}
            </div>
          </SwishPaymentFooter>
        </SwishPaymentFooterContent>
      }
    >
      <SwishPaymentDetails>
        <SwishPaymentLabel>{t("Tips.Method")}</SwishPaymentLabel>
        <SwishPaymentValue>{t("Tips.Swish")}</SwishPaymentValue>

        <SwishPaymentLabel>{t("Tips.TipsPeriod")}</SwishPaymentLabel>
        <SwishPaymentValue>{tipsPeriod}</SwishPaymentValue>

        <SwishPaymentLabel>{t("Tips.Recipients")}</SwishPaymentLabel>
        <SwishPaymentValue>{recipientsCount}</SwishPaymentValue>

        <SwishPaymentLabel>
          {t("Tips.TotalAmountToBePaidOut")}
        </SwishPaymentLabel>
        <SwishPaymentValue>
          {formatNumber(totalAmount)} {t("Tips.Currency")}
        </SwishPaymentValue>
      </SwishPaymentDetails>
    </Modal>
  );
};
